import { type ReactNode } from "react";
import clsx from "clsx";

/**
 * Accent palette available across the admin portal. Each maps to a
 * colour and a verb ("levels" = yellow, "users" = blue, "errors" = red).
 */
export type WidgetAccent =
  | "green"
  | "yellow"
  | "red"
  | "blue"
  | "orange"
  | "purple"
  | "neutral";

const ACCENT_HEX: Record<WidgetAccent, string> = {
  green: "#34C759",
  yellow: "#FFD60A",
  red: "#FF453A",
  blue: "#0A84FF",
  orange: "#FF9F0A",
  purple: "#AF52DE",
  neutral: "#8E8E93",
};

export function accentHex(accent: WidgetAccent): string {
  return ACCENT_HEX[accent];
}

interface WidgetProps {
  /** Accent colour — drives the rail + ring + colour-coded hover. */
  accent?: WidgetAccent;
  /** Span the entire grid row. */
  fullWidth?: boolean;
  /** Larger padding variant for hero / hero-adjacent widgets. */
  hero?: boolean;
  /** Compact padding for dense data tables. */
  compact?: boolean;
  /** Optional eyebrow label rendered above the title. */
  eyebrow?: ReactNode;
  /** Optional heading. */
  title?: ReactNode;
  /** Optional trailing slot — icons, buttons, etc. */
  trailing?: ReactNode;
  /** Optional subtitle (small grey text under the title). */
  subtitle?: ReactNode;
  /** Show the top accent rail. */
  rail?: boolean;
  children?: ReactNode;
  className?: string;
}

/**
 * iPhone-style widget surface. The bento grid primitive for the
 * admin "command center" — replaces the old `rounded-2xl border
 * border-white/10 bg-white/[0.02] p-5` card everywhere.
 */
export function Widget({
  accent = "neutral",
  fullWidth,
  hero,
  compact,
  eyebrow,
  title,
  subtitle,
  trailing,
  rail = true,
  children,
  className,
}: WidgetProps) {
  const accentColor = accentHex(accent);

  return (
    <div
      className={clsx(
        "fg-widget",
        hero && "fg-widget--hero",
        compact && "fg-widget--compact",
        fullWidth && "fg-span-4",
        className,
      )}
      style={
        {
          ["--ring-color" as string]: accentColor,
          ["--rail-from" as string]: accentColor,
          ["--rail-to" as string]: accentColor,
        } as React.CSSProperties
      }
    >
      {rail && accent !== "neutral" && (
        <span className="fg-widget__rail" aria-hidden />
      )}
      {(eyebrow || title || subtitle || trailing) && (
        <header className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            {eyebrow && <div className="fg-eyebrow mb-1">{eyebrow}</div>}
            {title && (
              <h3 className="text-base sm:text-lg font-bold tracking-tight text-white truncate">
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="mt-1 text-[12px] text-white/55 max-w-2xl">
                {subtitle}
              </p>
            )}
          </div>
          {trailing && <div className="shrink-0">{trailing}</div>}
        </header>
      )}
      <div className="flex-1 min-h-0 flex flex-col gap-3">{children}</div>
    </div>
  );
}

/** Accent-coloured icon container. */
export function AccentDot({
  accent,
  size = "md",
  children,
}: {
  accent: WidgetAccent;
  size?: "sm" | "md" | "lg";
  children: ReactNode;
}) {
  const accentColor = accentHex(accent);
  return (
    <span
      className={clsx(
        "fg-accent-dot",
        size === "sm" && "fg-accent-dot--sm",
        size === "lg" && "fg-accent-dot--lg",
      )}
      style={{ ["--accent" as string]: accentColor } as React.CSSProperties}
    >
      {children}
    </span>
  );
}

/** Pill-shaped badge. */
export function Pill({
  accent,
  children,
}: {
  accent: WidgetAccent;
  children: ReactNode;
}) {
  return (
    <span
      className="fg-pill"
      style={{ ["--pill-color" as string]: accentHex(accent) } as React.CSSProperties}
    >
      {children}
    </span>
  );
}

/** Bento grid container. 1/2/3/4 col responsive. */
export function Bento({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={clsx("fg-bento", className)}>{children}</div>;
}

/** Page hero with eyebrow + title + description + aside. */
export function PageHero({
  accent = "yellow",
  eyebrow,
  title,
  description,
  aside,
}: {
  accent?: WidgetAccent;
  eyebrow: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  aside?: ReactNode;
}) {
  const accentColor = accentHex(accent);
  return (
    <section
      className={clsx("fg-hero fg-span-4")}
      style={{ ["--hero-glow" as string]: `${accentColor}26` } as React.CSSProperties}
    >
      <div className="relative min-w-0 flex-1">
        <div
          className="fg-eyebrow mb-2"
          style={{ color: `${accentColor}cc` }}
        >
          {eyebrow}
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
          {title}
        </h1>
        {description && (
          <p className="mt-3 text-[15px] text-white/65 max-w-2xl leading-relaxed">
            {description}
          </p>
        )}
      </div>
      {aside && (
        <div className="relative flex flex-wrap gap-2 shrink-0">{aside}</div>
      )}
    </section>
  );
}